import { useCurrentUser } from '@shared/stores/current-user'
import {
  budgetFieldIsMandatory,
  budgetFieldIsOptional,
  getBudgetItemEnums,
  getRelationEnums,
} from 'src/pages/Budget/components/BudgetItemForm/BudgetItemForm.utils'
import type { ProxyRecord } from 'src/services/deserializers/deserializers.types'
import {
  getPersonEnums,
  personIsMandatory,
  personIsOptional,
  personIsReadOnly,
} from 'src/services/mappings/person/person.mapping.utils'
import {
  getOwnerNewEnums,
  getPortfolioEnums,
  getProductCategory,
  isBankNamePresent,
  portfolioIsMandatory,
  portfolioIsOptional,
  portfolioIsReadOnly,
} from 'src/services/mappings/portfolio/portfolio.mapping.utils'
import {
  getCspEnums,
  getCspLabel,
} from 'src/services/mappings/profession/profession.mapping.utils'
import type {
  DynamicEnumsRuleInterface,
  DynamicRuleInterface,
  FieldInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import {
  BUDGET_ITEM_FROM_TYPE_ASSOCIATION,
  BUDGET_ITEM_TO_TYPE_ASSOCIATION,
} from 'src/tpbp-core/models/budget/budgetItem.const'
import type { FIELD_STATUS_STATES } from 'src/tpbp-core/models/fields/fieldRule.types'
import {
  FIELD_RULES,
  type FieldStatus,
  type FieldStatusesByOriginKey,
} from 'src/tpbp-core/models/fields/fieldRules.const'
import { getAdditionalRules } from 'src/tpbp-json-api-handler/utils/ApiStore'
import { getPortfolioProductGroup } from 'src/services/PortfolioApiStore'
import { getPersonAge } from 'src/utils/helpers'
import { getEnumLabel } from 'src/utils/modelLabels.utils'

const stores = {
  useCurrentUser,
}

const constants = {
  BUDGET_ITEM_TO_TYPE_ASSOCIATION,
  BUDGET_ITEM_FROM_TYPE_ASSOCIATION,
}

const helpers = {
  getAdditionalRules,
  getPortfolioProductGroup,
  getProductCategory,
  getRelationEnums,
  getPortfolioEnums,
  getOwnerNewEnums,
  portfolioIsReadOnly,
  portfolioIsMandatory,
  portfolioIsOptional,
  isBankNamePresent,
  getPersonEnums,
  personIsMandatory,
  personIsOptional,
  personIsReadOnly,
  getEnumLabel,
  getCspEnums,
  getCspLabel,
  getPersonAge,
  budgetFieldIsMandatory,
  budgetFieldIsOptional,
  getBudgetItemEnums,
  ...constants,
  ...stores,
}

export function evalExpr(
  expr: boolean | undefined | Function,
  self: ProxyRecord | undefined,
  field: FieldInterface = {} as FieldInterface,
  context: Record<string, unknown> = {},
) {
  if (!expr) {
    return false
  }
  if (expr === true) {
    return true
  }

  const helpersWithContext = context ? { ...helpers, ...context } : helpers

  return expr(self, helpersWithContext, field)
}

function evalFieldStatusRulesForRule(
  fieldStatusStates: FIELD_STATUS_STATES,
  field: FieldInterface,
  rule: DynamicRuleInterface,
  data: ProxyRecord | undefined,
  fieldStatusesByOriginKey: FieldStatusesByOriginKey,
) {
  const value = evalExpr(rule.expression, data, field, {
    fieldStatusesByOriginKey,
  })

  if (!value) {
    return
  }

  switch (rule.type) {
    case FIELD_RULES.isReadOnly: {
      fieldStatusStates.isReadOnly = true

      break
    }
    case FIELD_RULES.isOptional: {
      fieldStatusStates.isOptional = true

      break
    }
    case FIELD_RULES.isMandatory: {
      fieldStatusStates.isMandatory = true

      break
    }
    case FIELD_RULES.isHidden: {
      fieldStatusStates.isHidden = true

      break
    }
    default: {
      break
    }
  }
}

export function getFirstValidRule(
  rules: DynamicEnumsRuleInterface[],
  data: ProxyRecord | undefined,
  field: FieldInterface,
  context: Record<string, unknown>,
): DynamicEnumsRuleInterface | undefined {
  return rules.find((rule) => evalExpr(rule.expression, data, field, context))
}

export function evalFieldStatusRules(
  field: FieldInterface,
  data: ProxyRecord | undefined,
  fieldStatusesByOriginKey: FieldStatusesByOriginKey,
): FieldStatus {
  const fieldStatusStates: FIELD_STATUS_STATES = {
    isHidden: false,
    isMandatory: false,
    isOptional: false,
    isReadOnly: false,
  }

  for (const rule of field.dynamicRules ?? []) {
    evalFieldStatusRulesForRule(
      fieldStatusStates,
      field,
      rule,
      data,
      fieldStatusesByOriginKey,
    )
  }

  return fieldStatusObjectToFieldStatus(fieldStatusStates)
}

function fieldStatusObjectToFieldStatus(
  fieldStatusStates: FIELD_STATUS_STATES,
): FieldStatus {
  if (fieldStatusStates.isHidden) {
    return FIELD_RULES.isHidden
  }

  if (fieldStatusStates.isReadOnly) {
    return FIELD_RULES.isReadOnly
  }

  if (fieldStatusStates.isMandatory) {
    return FIELD_RULES.isMandatory
  }

  if (fieldStatusStates.isOptional) {
    return FIELD_RULES.isOptional
  }

  return false
}
